/**
 * Static import bundles for all locale namespaces. i18next consumes the
 * `resources` shape directly; next-intl loads via `getMessages(locale)`.
 *
 * Adding a new namespace:
 *  1. Create `src/locales/<locale>/<namespace>.json` for all locales
 *  2. Add a `<namespace>: enX` entry below
 *  3. Both apps pick it up automatically
 */

import enCommon from './locales/en/common.json' with { type: 'json' };
import enAuth from './locales/en/auth.json' with { type: 'json' };
import enOrders from './locales/en/orders.json' with { type: 'json' };
import enOts from './locales/en/ots.json' with { type: 'json' };
import enAdmin from './locales/en/admin.json' with { type: 'json' };
import enPatient from './locales/en/patient.json' with { type: 'json' };
import enMarketing from './locales/en/marketing.json' with { type: 'json' };
import enEmail from './locales/en/email.json' with { type: 'json' };
import enErrors from './locales/en/errors.json' with { type: 'json' };
import enProvider from './locales/en/provider.json' with { type: 'json' };

import arCommon from './locales/ar/common.json' with { type: 'json' };
import arAuth from './locales/ar/auth.json' with { type: 'json' };
import arOrders from './locales/ar/orders.json' with { type: 'json' };
import arOts from './locales/ar/ots.json' with { type: 'json' };
import arAdmin from './locales/ar/admin.json' with { type: 'json' };
import arPatient from './locales/ar/patient.json' with { type: 'json' };
import arMarketing from './locales/ar/marketing.json' with { type: 'json' };
import arEmail from './locales/ar/email.json' with { type: 'json' };
import arErrors from './locales/ar/errors.json' with { type: 'json' };
import arProvider from './locales/ar/provider.json' with { type: 'json' };

import esCommon from './locales/es/common.json' with { type: 'json' };
import esAuth from './locales/es/auth.json' with { type: 'json' };
import esOrders from './locales/es/orders.json' with { type: 'json' };
import esOts from './locales/es/ots.json' with { type: 'json' };
import esAdmin from './locales/es/admin.json' with { type: 'json' };
import esPatient from './locales/es/patient.json' with { type: 'json' };
import esMarketing from './locales/es/marketing.json' with { type: 'json' };
import esEmail from './locales/es/email.json' with { type: 'json' };
import esErrors from './locales/es/errors.json' with { type: 'json' };
import esProvider from './locales/es/provider.json' with { type: 'json' };

import type { Locale } from './locale-list';

export const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    orders: enOrders,
    ots: enOts,
    admin: enAdmin,
    patient: enPatient,
    marketing: enMarketing,
    email: enEmail,
    errors: enErrors,
    provider: enProvider,
  },
  ar: {
    common: arCommon,
    auth: arAuth,
    orders: arOrders,
    ots: arOts,
    admin: arAdmin,
    patient: arPatient,
    marketing: arMarketing,
    email: arEmail,
    errors: arErrors,
    provider: arProvider,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    orders: esOrders,
    ots: esOts,
    admin: esAdmin,
    patient: esPatient,
    marketing: esMarketing,
    email: esEmail,
    errors: esErrors,
    provider: esProvider,
  },
} as const;

export type Namespace = keyof (typeof resources)['en'];

/**
 * Flat-merged messages for a given locale (used by next-intl).
 * Namespace keys are nested: `common.action_save`.
 */
export function getMessages(locale: Locale): Record<string, unknown> {
  return resources[locale];
}
